import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles, styled, useTheme } from '@material-ui/core/styles';
import {
  AppBar, Toolbar, Box, Typography,
  IconButton, Badge, MenuItem,
  FormControl, Select, Drawer, useMediaQuery,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCartOutlined';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { cartAddAction } from '../../../actions/license.action';
import ColIcon from '../../../assets/img/marketPlace/col.svg';
import EcuIcon from '../../../assets/img/marketPlace/ecu.svg';
import CustomDialog from '../Modal';
import { TENANT_MARKET_CONFIG, API_SEGURIDAD } from '../../../config/config';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.header.appBar.backgroundColor,
    boxShadow: 'none !important',
    borderBottom: 'none !important',
    outline: 'none',
    width: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: '80.5px',
    paddingRight: '80.5px',
    minHeight: '64px',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: '5%',
      paddingRight: '5%',
    },
  },
  styleLogo: {
    display: 'block',
    width: '91.72px',
    height: '32px',
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
  },
  countrySelector: {
    display: 'flex',
    alignItems: 'center',
    margin: '5px',
    cursor: 'pointer',
    marginRight: '20px !important',
    '&:hover': { borderRadius: '4px', backgroundColor: theme.header.countrySelector.hover },
    '& .MuiSvgIcon-root': { color: theme.header.countrySelector.icon },
    '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  mobileCountryItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    width: '100%',
    borderRadius: '8px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'background 0.3s',
  },
  currencyText: {
    marginRight: theme.spacing(2),
    fontWeight: 500,
    fontSize: '0.9rem',
    color: theme.header.currencyText.color,
    whiteSpace: 'nowrap',
  },
  cartButton: {
    padding: 8,
  },
  badgeItems: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.header.badgeItems.backgroundColor,
      color: theme.header.badgeItems.color,
      fontWeight: 600,
      fontFamily: '"Karla"',
      fontSize: '12px',
    },
  },
  drawerPaper: {
    width: '100%',
    backgroundColor: theme.header.appBar.backgroundColor,
    padding: '20px',
    borderBottom: `1px solid ${theme.header.countrySelector.hover}`,
  },
  menuItemRoot: {
    '&:hover': {
      backgroundColor: `${theme.header.menu.selectedItem.hoverBackgroundColor} !important`,
    },
  },
  selectedItem: {
    backgroundColor: `${theme.header.menu.selectedItem.backgroundColor} !important`,
    '&:hover': {
      backgroundColor: `${theme.header.menu.selectedItem.hoverBackgroundColor} !important`,
    },
  },
}));

const OwnTypography = styled(Typography)(({ theme }) => ({
  color: theme.header.menu.typography,
  fontWeight: 520,
}));

const COUNTRY_ICONS = {
  COL: ColIcon,
  ECU: EcuIcon,
};

const Header = (props) => {
  const {
    cartData, isAssignment, getParametersMarket, marketContext, tenant, onHeaderReady,
    code, cartAdd, licenseLoadingSaveOrder, loadingViewMore,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767px)');

  const isSystemBusy = loadingViewMore || licenseLoadingSaveOrder;

  const [isDialogOpen, setIsDialogOpen] = useState({ open: false, targetLabel: '', targetUrl: '' });
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const allowedCountriesString = getParametersMarket?.find(
    (param) => param.parameterCode === 'Show_Links_Market' && param.parameterKey?.toUpperCase() === tenant?.toUpperCase(),
  )?.parameterValue;

  const allowedCountries = allowedCountriesString ? allowedCountriesString.split(',') : [];

  const dynamicCountries = getParametersMarket
    ? getParametersMarket
      .filter((param) => {
        const isUrlMarket = param.parameterCode === 'URL_Market';
        const isAllowed = allowedCountries.length > 0
          ? allowedCountries.includes(param.parameterKey.toUpperCase())
          : true;
        return isUrlMarket && isAllowed;
      })
      .map((param) => ({
        id: param.parameterKey.toUpperCase(),
        label: param.parameterKey.charAt(0) + param.parameterKey.slice(1).toLowerCase(),
        url: param.parameterValue,
        icon: COUNTRY_ICONS[param.parameterKey.toUpperCase()] || ColIcon,
        description: param?.parameterDescription,
      }))
    : [];

  const currency = cartData?.currency || '---';
  const totalItems = cartData?.cartItems?.reduce((acc, item) => acc + (item.quantity || 0), 0) || 0;
  const currentCountryCode = (
    cartData?.countryCode
    || marketContext?.countryCode
    || TENANT_MARKET_CONFIG[tenant]?.countryCode
  ).toUpperCase();

  const validatedValue = dynamicCountries.some((c) => c.id === currentCountryCode)
    ? currentCountryCode
    : '';

  const handleCountryChange = (selectedId) => {
    if (isSystemBusy) return;
    const selectedCountry = dynamicCountries.find((c) => c.id === selectedId);

    if (selectedId !== currentCountryCode) {
      setIsDialogOpen({
        open: true,
        targetLabel: selectedCountry?.label.toUpperCase(),
        targetUrl: selectedCountry?.url,
      });
      if (isMobile) setMenuOpen(false);
    }
  };

  const establishCountry = async () => {
    if (loading || isSystemBusy) return;
    setLoading(true);
    const payload = {
      countryCode: cartData?.countryCode,
      regionCode: cartData?.regionCode,
      transactionCode: code,
      cartItems: [],
    };
    try {
      await cartAdd(payload);
      if (isDialogOpen.targetUrl) {
        window.location.href = `${API_SEGURIDAD}/api/redirect/verify?url=${encodeURI(isDialogOpen.targetUrl)}`;
      }
    } catch (error) {
      setLoading(false);
      window.location.href = `${API_SEGURIDAD}/api/redirect/verify?url=${encodeURI(isDialogOpen.targetUrl)}`;
    }
  };

  const isHeaderDataReady = dynamicCountries.length > 0 && !!currentCountryCode;

  useEffect(() => {
    if (isHeaderDataReady) {
      onHeaderReady();
    }
  }, [isHeaderDataReady, onHeaderReady]);

  const ActionItems = ({ inMobile = false }) => (
    <Box
      style={{
        display: 'flex',
        flexDirection: inMobile ? 'column' : 'row',
        alignItems: inMobile ? 'flex-start' : 'center',
        width: inMobile ? '100%' : 'auto',
        opacity: isSystemBusy ? 0.6 : 1,
        pointerEvents: isSystemBusy ? 'none' : 'auto',
        fontFamily: '"Raleway"',
      }}
    >
      {isAssignment ? (
        currentCountryCode && (
          <Box style={{
            display: 'flex', alignItems: 'center', marginRight: inMobile ? '0' : '20px', padding: inMobile ? '10px 0' : '0', fontFamily: '"Raleway"',
          }}
          >
            <img
              alt="pais"
              src={dynamicCountries.find((o) => o.id === currentCountryCode)?.icon}
              style={{
                width: 22, height: 'auto', borderRadius: 2, marginRight: '12px',
              }}
            />
            <Typography style={{ color: theme.header.colorText, fontWeight: 500, fontFamily: '"Raleway"' }}>
              {dynamicCountries.find((o) => o.id === currentCountryCode)?.label}
            </Typography>
          </Box>
        )
      ) : (
        <>
          {inMobile ? (
            <Box style={{ width: '100%', marginBottom: '16px', fontFamily: '"Raleway"' }}>
              <Typography
                variant="caption"
                style={{
                  color: theme.header.colorText, opacity: 0.7, marginLeft: '16px', marginBottom: '8px', display: 'block', textTransform: 'uppercase', fontFamily: '"Raleway"',
                }}
              >
                Seleccionar País
              </Typography>
              {dynamicCountries.map((opt) => (
                <Box
                  key={opt.id}
                  className={classes.mobileCountryItem}
                  onClick={() => handleCountryChange(opt.id)}
                  style={{
                    backgroundColor: validatedValue === opt.id ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                    border: validatedValue === opt.id ? `1px solid ${theme.header.countrySelector.hover}` : '1px solid transparent',
                    fontFamily: '"Raleway"',
                  }}
                >
                  <img alt="countries" src={opt.icon} style={{ width: 22, marginRight: '12px' }} />
                  <OwnTypography style={{ flexGrow: 1, color: theme.header.colorText, fontFamily: '"Raleway"' }}>{opt?.description || opt.label}</OwnTypography>
                  {validatedValue === opt.id && (
                    <Box style={{
                      width: 8, height: 8, borderRadius: '50%', backgroundColor: theme.palette.primary.main, fontFamily: '"Raleway"',
                    }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box className={classes.countrySelector} style={{ width: 'auto', margin: '5px', fontFamily: '"Raleway"' }}>
              <FormControl>
                <Select
                  disableUnderline
                  value={validatedValue}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  disabled={isSystemBusy}
                  IconComponent={KeyboardArrowDownIcon}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                    PaperProps: {
                      style: {
                        width: 148,
                        paddingLeft: '10px',
                        paddingRight: '10px',
                        borderRadius: '6px',
                        marginTop: '8px',
                      },
                    },
                  }}
                  renderValue={(selected) => {
                    const item = dynamicCountries.find((o) => o.id === selected);
                    if (!item) return null;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          alt="pais"
                          src={item?.icon}
                          style={{
                            width: 16, height: 'auto', borderRadius: 2, marginRight: '8px',
                          }}
                        />
                        <Box component="span" sx={{ color: theme.header.colorText, fontSize: '16px', fontFamily: '"Raleway"' }}>{item?.label}</Box>
                      </Box>
                    );
                  }}
                >
                  {dynamicCountries.map((opt) => (
                    <MenuItem
                      key={opt.id}
                      value={opt.id}
                      style={{
                        borderRadius: '8px',
                        margin: '2px 0',
                        fontFamily: '"Raleway"',
                      }}
                      classes={{
                        selected: classes.selectedItem,
                        root: classes.menuItemRoot,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img alt="countries" src={opt.icon} style={{ width: 16 }} />
                        <OwnTypography style={{ paddingLeft: '8px', color: theme.header.menu.items.typography, fontFamily: '"Raleway"' }}>{opt?.description || opt.label}</OwnTypography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </>
      )}

      {!isAssignment && (
        <>
          <Typography
            style={{
              marginRight: inMobile ? '0' : '26px',
              padding: inMobile ? '10px 16px' : '0',
              color: theme.header.colorText,
              display: 'flex',
              alignItems: 'center',
              fontSize: '16px',
              fontFamily: '"Raleway"',
              fontWeight: 500,
            }}
          >
            <span>Moneda:&nbsp;</span>
            {currency}
          </Typography>

          {inMobile ? (
            <Box style={{
              display: 'flex', alignItems: 'center', padding: '10px 16px', fontFamily: '"Raleway"',
            }}
            >
              <ShoppingCartIcon style={{ color: theme.header.colorText, marginRight: '8px' }} />
              <Typography style={{ color: theme.header.colorText, fontWeight: 500, fontFamily: '"Raleway"' }}>
                Carrito (
                {totalItems}
                )
              </Typography>
            </Box>
          ) : (
            <IconButton className={classes.cartButton} disabled={isSystemBusy}>
              <Badge badgeContent={totalItems} className={classes.badgeItems} overlap="rectangular" max={totalItems}>
                <ShoppingCartIcon style={{ color: theme.header.colorText }} />
              </Badge>
            </IconButton>
          )}
        </>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" className={classes.appBar} elevation={0}>
        <Toolbar className={classes.toolbar}>
          <Box>
            {theme?.header?.logo ? (
              <img
                src={theme.header.logo}
                alt="logo"
                className={classes.styleLogo}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <Box style={{ width: '115px' }} />
            )}
          </Box>

          {isMobile && !isAssignment ? (
            <IconButton
              onClick={() => !isSystemBusy && setMenuOpen(!menuOpen)}
              style={{ color: theme.header.colorText }}
              disabled={isSystemBusy}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          ) : (
            <ActionItems />
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="top"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        classes={{ paper: classes.drawerPaper }}
        style={{ zIndex: 1000 }}
      >
        <Box style={{ marginTop: '60px' }}>
          <ActionItems inMobile />
        </Box>
      </Drawer>

      <CustomDialog
        textTitle="¡Vas a cambiar de país!"
        textCloseDialog="Cancelar"
        maxWidthValue={608}
        isOpen={isDialogOpen.open}
        setIsOpen={(val) => {
          if (loading || isSystemBusy) return;
          setIsDialogOpen({ ...isDialogOpen, open: val });
        }}
        acceptAction={establishCountry}
        textAccept={(loading || isSystemBusy) ? 'Procesando...' : 'Si, cambiar'}
        acceptButtonStyle={{
          backgroundColor: theme.body.dialog.colorIcon,
          opacity: (loading || isSystemBusy) ? 0.5 : 1,
          cursor: (loading || isSystemBusy) ? 'not-allowed' : 'pointer',
        }}
        cancelButtonStyle={{
          backgroundColor: theme.palette.primary.contrastText,
          border: `1px solid ${theme.body.dialog.colorIcon}`,
          color: theme.body.dialog.colorIcon,
        }}
        textSizeTitle={28}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5.5,
          mb: 2,
          letterSpacing: 0,
        }}
        >
          <Typography style={{ fontFamily: '"Raleway"', fontSize: 48, fontWeight: 700 }}>
            {currentCountryCode}
          </Typography>

          <ArrowForwardIcon style={{
            color: theme.palette.primary.main,
            fontSize: 37,
            marginTop: '2px',
          }}
          />
          <Typography style={{ fontFamily: '"Raleway"', fontSize: 48, fontWeight: 700 }}>
            {isDialogOpen.targetLabel}
          </Typography>
        </Box>
        <Typography style={{
          fontFamily: '"Raleway"',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '1.5',
          marginBottom: '10px',
        }}
        >
          Al hacerlo, se eliminarán los productos configurados en tu carrito actual.
        </Typography>
      </CustomDialog>
    </>
  );
};
const mapStateToProps = (props) => ({
  cartData: props.license.cartData,
  getParametersMarket: props.general.getParametersMarket,
  licenseLoadingSaveOrder: props.license.licenseLoadingSaveOrder,
});

const mapDispatchToProps = {
  cartAdd: cartAddAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Header);
